import React, {useState, useEffect, useRef} from "react";
import {GridContainer, gridConstants, GridCell} from "../../../Components/Grid";
import {
    Button,
    CheckBoxField,
    clearInputFormErrorFields,
    convertInputFormToObject,
    EmailField,
    InputForm,
    SelectField,
    SwitchFieldset,
    TextField,
    useInputForm,
} from "../../../Components/Inputs";
import {reinitializeSpecificInputFormFields} from "../../../Components/Inputs/inputUtility";
import {userDetailsModel} from "./userDetailsFactory";
import {createListFromArrayOfObjects} from "../../../Utilities/Types/listUtilities";
import {UserDetailsPasswordFields} from "./UserDetailsPasswordFields";
import {
    canUserUpdateApprovalStatus,
    createPatronRoleId,
    getWarningMessageForUserDetailsForm,
    isUserDetailsFormValid,
    isLoggedInUserSameAsPatron,
    isDistrictSelectionOther,
} from "./userDetailsUtilities";
import {isNullOrUndefined} from "../../../Utilities/commonUtilities";
import {
    createSubtypeListFromPatronRole,
    JOB_ROLES,
    ROLES_WITHOUT_SUBTYPES,
} from "../../../SiteConfig/jobTitles";
import {APPROVAL_STATUS_LIST} from "../approvalConstants";
import {ACTIVE_STATUS_LIST} from "../activeConstants";
import { isAdmin } from "../../../Utilities/LocalStorage/storageUtilities";
import {PATRON_SUBTYPE} from "../../../Components/Authorize/roleConstants";

import type {User} from "../../../types";

interface UserDetailsProps {
    askToUpdatePassword?: boolean;
    districts: { id: string; districtName: string }[];
    handleCancel?: () => void;
    handleManageUser: (user: Record<string, unknown>) => void;
    isEditing?: boolean;
    isLoading?: boolean;
    jobTitles: { jobID: number; jobTitle: string }[];
    patron: User;
    setWarnings: (warnings: string[]) => void;
}

export const UserDetails: React.FC<UserDetailsProps> = ({
                         askToUpdatePassword,
                         districts,
                         handleCancel,
                         handleManageUser,
                         isEditing = false,
                         jobTitles,
                         patron,
                         setWarnings,
                     }) => {

    const prevJobTitle = useRef(String(patron.jobTitle ?? ''));
    const prevPatronSubType = useRef(String(patron.patronSubType ?? ''));

    const userDetailsForm = useInputForm(userDetailsModel({...patron, ...{askToUpdatePassword}}));
    const prevDistrictId = useRef(String(userDetailsForm.districtId.value ?? ''));


    const [enablePasswordFields, setEnablePasswordFields] = useState(
        !userDetailsForm.askToUpdatePassword.value
    );

    const showOrganizationByDistrict = userDetailsForm.districtId.value && isDistrictSelectionOther(String(userDetailsForm.districtId.value), districts);

    const isFormValidAndCheckForWarning = () => {
        const isFormValid = isUserDetailsFormValid(userDetailsForm, setWarnings, enablePasswordFields, isEditing, patron, districts);

        if (isFormValid && isEditing && !isAdmin()) {
            let warningMessages = getWarningMessageForUserDetailsForm(userDetailsForm, patron);

            if (warningMessages.length > 0) {
                const message = `Please note:\n\n${warningMessages.join('\n\n')} \n\nAre you sure you want to continue?`;
                return confirm(message);
            }
        }

        return isFormValid;
    };

    const handleClickSubmit = () => {
        if (isFormValidAndCheckForWarning()) {

            if (!isEditing)
                createPatronRoleId(userDetailsForm);

            const user = convertInputFormToObject(userDetailsForm);

            handleManageUser(user);
        }
    };

    const handleClickCancel = () => {
        handleCancel?.();
    }

    const handleUpdatePasswordCheck = () => {
        setEnablePasswordFields(!enablePasswordFields);
        userDetailsForm.updatePassword.setValue(
            userDetailsForm.updatePassword.value === 'true' ? 'false' : 'true'
        );
    };

    const resetFormFields = (clearValuesForFields: string[], newValue = "") => {
        reinitializeSpecificInputFormFields(userDetailsForm, clearValuesForFields, newValue);
        clearInputFormErrorFields(userDetailsForm);
    };

    useEffect(
        () => {

            if (userDetailsForm.email.value === patron.email) {
                resetFormFields(["confirmEmail"]);
            }

            //clears the sub-forms when the job title changes
            if (!isNullOrUndefined(prevJobTitle.current) && prevJobTitle.current !== userDetailsForm.jobTitle.value) {

                resetFormFields(["patronSubType", "organization", "districtId"]);
                prevJobTitle.current = userDetailsForm.jobTitle.value;

                // Auto-assign PatronSubType.None for roles without subtypes
                if (ROLES_WITHOUT_SUBTYPES.includes(userDetailsForm.jobTitle.value)) {
                    userDetailsForm.patronSubType.setValue(String(PATRON_SUBTYPE.None));
                }
            }

            //clears the sub-forms when the patronSubType changes
            if (!isNullOrUndefined(prevPatronSubType.current) && prevPatronSubType.current !== userDetailsForm.patronSubType.value) {

                resetFormFields(["organization", "districtId"]);
                prevPatronSubType.current = userDetailsForm.patronSubType.value;
            }

            // clears organization when district changes away from Other
            if (!isNullOrUndefined(prevDistrictId.current) && prevDistrictId.current !== userDetailsForm.districtId.value) {
                if (!isDistrictSelectionOther(String(userDetailsForm.districtId.value), districts)) {
                    resetFormFields(["organization"]);
                }

                prevDistrictId.current = userDetailsForm.districtId.value;
            }
        },
        [userDetailsForm.jobTitle.value, userDetailsForm.patronSubType.value, userDetailsForm.email.value, userDetailsForm.districtId.value]
    );

    return (
        <InputForm name={"userDetails"}>

            <TextField {...userDetailsForm.firstName} />

            <TextField {...userDetailsForm.lastName} />

            <EmailField {...userDetailsForm.email} helpText={""}/>

            {
                isEditing && userDetailsForm.email.value !== patron.email &&
                <EmailField {...userDetailsForm.confirmEmail}
                            helpText={`<p style="color: red;">Please note: If you change your email address, you won't be able to log in using your old email. Please use your new email address to access your account.</p>`}/>
            }

            <SelectField
                {...userDetailsForm.jobTitle}
                defaultOption={"Select a Job Title"}
                includeDefaultOption={true}
                options={createListFromArrayOfObjects(jobTitles, "jobID", "jobTitle")}
                label={"Job Title"}
            />

            {/* Roles WITH subtypes: show subtype dropdown */}
            {
                userDetailsForm.jobTitle.value &&
                !ROLES_WITHOUT_SUBTYPES.includes(userDetailsForm.jobTitle.value) &&
                <>
                    <SelectField
                        {...userDetailsForm.patronSubType}
                        defaultOption={"Select a Role"}
                        includeDefaultOption={true}
                        options={createSubtypeListFromPatronRole(Number(userDetailsForm.jobTitle.value))}
                        label={"Role"}
                    />

                    {/* District dropdown for all roles with subtypes except RMTC */}
                    {
                        userDetailsForm.patronSubType.value &&
                        userDetailsForm.jobTitle.value !== JOB_ROLES.Rmtc &&
                        <>
                            <SelectField
                                {...userDetailsForm.districtId}
                                defaultOption={"Select a District"}
                                includeDefaultOption={true}
                                options={createListFromArrayOfObjects(
                                    districts,
                                    "id",
                                    "districtName"
                                )}
                                label={"District"}
                            />

                            {showOrganizationByDistrict && <TextField {...userDetailsForm.organization} />}
                        </>
                    }
                </>
            }

            {/* Roles WITHOUT subtypes: show district + organization */}
            {
                userDetailsForm.jobTitle.value &&
                ROLES_WITHOUT_SUBTYPES.includes(userDetailsForm.jobTitle.value) &&
                <>
                    <SelectField
                        {...userDetailsForm.districtId}
                        defaultOption={"Select a District"}
                        includeDefaultOption={true}
                        options={createListFromArrayOfObjects(
                            districts,
                            "id",
                            "districtName"
                        )}
                        label={"District"}
                    />

                    <TextField {...userDetailsForm.organization} />
                </>
            }

            {
                !isEditing &&
                <>

                    {
                        userDetailsForm.askToUpdatePassword.value &&
                        <SwitchFieldset
                            {...userDetailsForm.updatePassword}
                            onChange={handleUpdatePasswordCheck}
                            label={`enable password update`}
                            legend={`Update Password`}
                        >
                            <UserDetailsPasswordFields
                                enablePasswordFields={enablePasswordFields}
                                userDetailsForm={userDetailsForm}
                            />
                        </SwitchFieldset>
                    }
                    {
                        !userDetailsForm.askToUpdatePassword.value &&
                        <UserDetailsPasswordFields
                            enablePasswordFields={enablePasswordFields}
                            userDetailsForm={userDetailsForm}
                        />
                    }
                    <CheckBoxField
                        name="floridaShippingConfirmed"
                        label="I understand that physical materials from the loan library are only shipped to addresses within the state of Florida."
                        checked={userDetailsForm.floridaShippingConfirmed.value === "true"}
                        error={userDetailsForm.floridaShippingConfirmed.error}
                        className="florida-shipping-checkbox"
                        onChange={(e) => {
                            userDetailsForm.floridaShippingConfirmed.setValue(e.target.checked ? "true" : "");
                        }}
                    />

                </>
            }

            {
                isEditing &&
                <>
                    <SelectField
                        {...userDetailsForm.approvalStatus}
                        includeDefaultOption
                        options={APPROVAL_STATUS_LIST}
                        defaultOptionValue={""}
                        defaultOption={"Select an approval status"}
                        disabled={!canUserUpdateApprovalStatus(patron)}
                    />

                    {
                        isAdmin() &&
                        <SelectField
                            {...userDetailsForm.isActive}
                            includeDefaultOption={false}
                            options={ACTIVE_STATUS_LIST}
                            label={"Active Status"}
                            disabled={isLoggedInUserSameAsPatron(patron)}
                        />
                    }
                </>
            }

            <GridCell>
                <GridContainer className={`horizontal-center`} hasMarginX hasMarginY isFlush>
                    <>
                        <GridCell small_columns={isEditing ? gridConstants.column.SIX : gridConstants.column.TWELVE}>
                            <Button
                                isPrimary
                                label={`Submit`}
                                name={`btnSubmit`}
                                onClick={handleClickSubmit}
                            />
                        </GridCell>

                        {
                            isEditing &&
                            <GridCell medium_columns={gridConstants.column.SIX}>
                                <Button
                                    label={`Cancel`}
                                    name={`btnCancel`}
                                    onClick={handleClickCancel}
                                />
                            </GridCell>
                        }
                    </>
                </GridContainer>
            </GridCell>
        </InputForm>
    );
};

